import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

// components
import Button from "@surgia/ui/components/button";
import GenericModal from "@surgia/ui/components/genericModal";
import HeaderLayout from "@surgia/ui/layouts/Header";
import { PurchaseOrderTableUntitled } from "../../containers/tables/PurchaseOrderTableUntitled";
import CreatePurchaseOrderModal from "../../containers/Modals/CreatePurchaseOrderModal";
import { useOutletContext } from "react-router-dom";
import { sharedQueries } from "@surgia/api/src/queryStore/shared";
import { HUB_PROTECTED_ROUTES } from "../../routes"; // Add routes import
import { permissions } from "../../permissions";
import config from "@surgia/config";

// icons
import addIcon from "@surgia/ui/icons/actions/add.svg";

// constants
const ITEMS_PER_PAGE = 8;
// ✅ Migrated: branchesIds moved inside component to use Redux

// Filters Modal Component
export const FiltersModal = ({
  open,
  onClose,
  onApply,
  filters,
  setFilters,
}: {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
  filters: any;
  setFilters: (f: any) => void;
}) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = React.useState(filters);

  // Keep localFilters in sync with parent filters when modal opens
  React.useEffect(() => {
    if (open) setLocalFilters(filters);
  }, [open, filters]);

  const handleApply = () => {
    setFilters(localFilters);
    onApply();
  };

  const handleClear = () => {
    setLocalFilters({
      ...localFilters,
      id: "",
      branches_ids: "",
    });
  };

  return (
    <GenericModal
      title={t("filters")}
      open={open}
      onClose={onClose}
      footer={
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={handleClear}
            className="text-[#0033CC] font-medium"
          >
            {t("admin.clearFilters")}
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={handleApply}>
            {t("filter")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* ID Filter */}
        <div>
          <label className="block text-[14px] mb-1">
            {t("shared.table.id")}
          </label>
          <div className="relative">
            <input
              type="text"
              className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-gray-300"
              placeholder={t("admin.idRange")}
              value={localFilters.id || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[\d-]*$/.test(value) || value === "") {
                  setLocalFilters({ ...localFilters, id: value });
                }
              }}
              inputMode="numeric"
              pattern="[\d-]*"
            />
          </div>
        </div>
      </div>
    </GenericModal>
  );
};

// Main Purchase Order Component
const PurchaseOrders: React.FC = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate(); // Add this line
  const authState = useSelector((state: RootState) => state.auth);

  // Add user permissions selector
  const userPermissions = authState.globalState.user_permissions?.map(
    (permission) => permission.name
  ) || [];

  // ✅ Migrated from module-level localStorage to Redux
  const branchesIds = authState.globalState.user_branches?.split(',').map(Number).filter(Boolean) || [];
  const allBranchesIds = branchesIds.join(",");

  interface OutletContextType {
    branchesDropdown: any[];
    selectedBranch: string | null;
    setSelectedBranch: (branch: string | null) => void;
    isbranchesDropDownLoading: boolean;
  }

  const {
    branchesDropdown,
    selectedBranch,
    setSelectedBranch,
    isbranchesDropDownLoading,
  } = useOutletContext<OutletContextType>();

  // Add draft PO query
  const { data: draftPO, isPending: isDraftPOPending } = useQuery({
    ...sharedQueries.purchaseOrder.queries.getCurrentDraftPO({
      tenantId: authState.globalState.tenant_id || 0,
      componentId: String(config.componentId),
    }),
    enabled: !!(
      authState.globalState.tenant_id &&
      config.componentId
    ),
  });
  const isDraftPOEmpty = !draftPO && !isDraftPOPending;

  // State management
  const [currentPage, setCurrentPage] = useState(0);
  const [openFilters, setOpenFilters] = useState(false);
  const [status, setStatus] = useState<string | undefined>("SUBMITTED");
  const [search] = useState("");
  const [filters, setFilters] = useState({
    id: "",
    branches_ids: "",
  });
  const [selectedPurchaseOrders, setSelectedPurchaseOrders] = useState<
    string[]
  >([]);
  const [showCreatePOModal, setShowCreatePOModal] = useState(false);

  // Add sort tracking state
  const [currentSort, setCurrentSort] = useState<{
    column: string | null;
    direction: string | null;
  }>({ column: null, direction: null });

  // Track sort changes
  useEffect(() => {
    const sortParam = searchParams.get("sort");
    if (sortParam) {
      const [column, direction] = sortParam.split(":");
      const newSort = { column, direction };

      // Only log if the sort has actually changed
      if (
        currentSort.column !== column ||
        currentSort.direction !== direction
      ) {
        setCurrentSort(newSort);
      }
    }
  }, [searchParams]);

  // Fetch purchase orders data
  const { data: purchaseOrdersResponse, isLoading } = useQuery({
    ...sharedQueries.purchaseOrder.queries.getPurchaseOrdersList({
      componentId: String(config.componentId),
      tenantId: authState.globalState.tenant_id?.toString() || "",
      branchesIds: selectedBranch || "null",
      sort: (() => {
        const sortParam = searchParams.get("sort");
        if (sortParam) {
          const [column, direction] = sortParam.split(":");
          // Map "ascending" to "asc" and "descending" to "desc"
          const mappedDirection = direction === "ascending" ? "asc" : "desc";
          return `${column},${mappedDirection}`;
        }
        // Default sort: id descending
        return "id,desc";
      })(),
      page: currentPage,
      size: ITEMS_PER_PAGE,
    }),
    enabled: !!(
      config.componentId &&
      authState.globalState.tenant_id
    ),

    select: (data) => {
      if ("errorCode" in data) {
        return {
          data: [],
          headers: {},
        };
      }
      if (Array.isArray(data.data)) {
        const transformed = data.data.map((po: any) => ({
          id: po.id,
          createdBy: po.created_by,
          branch: po.branch?.name || "N/A",
          totalQuantity: po.total_quantity,
          "orderSummary.total": po.order_summary?.total || 0,
          createdAt: po.created_at,
          status: po.status,
          branchId: po.branch?.id || null,
          numberOfPurchaseRequests: po.number_of_purchase_requests,
        }));
        return {
          data: transformed,
          headers: data.headers,
        };
      }
      return {
        data: [],
        headers: {},
      };
    },
  });

  useEffect(() => {
    setCurrentPage(0);
  }, [search, status, filters.id]);

  const handleViewPODetails = (poId: string, branchId: string) => {
    console.log("View PO details:", poId, branchId);
    // Navigate to the Purchase Order Details View page
    navigate(HUB_PROTECTED_ROUTES.PURCHASE_ORDER_VIEW.replace(":id", poId));
  };

  const handleCreatePO = () => {
    if (draftPO && !("errorCode" in draftPO) && draftPO?.items?.length == 0) {
      setShowCreatePOModal(true);
      return;
    }
    navigate(HUB_PROTECTED_ROUTES.PURCHASE_ORDER_CREATION);
  };
  return (
    <div dir={isRTL ? "rtl" : "ltr"} className="">
      <HeaderLayout
        title="Purchase Orders"
        subtitle="Manage and monitor all POs"
        additionalStyles="justify-between"
        showBranchesDropDown={allBranchesIds?.includes(",")}
        branches={branchesDropdown}
        selectedBranch={selectedBranch}
        setSelectedBranch={setSelectedBranch}
        allUserBranches={allBranchesIds}
        showAllBranches={true}
        isBranchesLoading={isbranchesDropDownLoading}
      >
        {userPermissions.includes(
          permissions.EDIT_DRAFT_PURCHASE_ORDER ||
            permissions.VIEW_DRAFT_PURCHASE_ORDER
        ) && (
          <Button
            variant="primary"
            startIcon={addIcon}
            onClick={handleCreatePO}
            disabled={
              isDraftPOPending ||
              (!userPermissions.includes(
                permissions.EDIT_DRAFT_PURCHASE_ORDER
              ) &&
                !!draftPO)
            }
          >
            {isDraftPOPending
              ? "Loading..."
              : !userPermissions.includes(
                    permissions.EDIT_DRAFT_PURCHASE_ORDER
                  ) && !!isDraftPOEmpty
                ? "View PO"
                : !!isDraftPOEmpty
                  ? "Create PO"
                  : "Edit PO "}
          </Button>
        )}
      </HeaderLayout>

      {/* Purchase Orders Table Section using Untitled UI */}
      <PurchaseOrderTableUntitled
        data={purchaseOrdersResponse?.data || []}
        loading={isLoading}
        currentPage={currentPage}
        totalPages={Math.ceil(
          (purchaseOrdersResponse?.headers?.["x-total-count"] || 0) /
            ITEMS_PER_PAGE
        )}
        onPageChange={setCurrentPage}
        showBranchColumn={selectedBranch?.includes(",")}
        selectedPurchaseOrders={selectedPurchaseOrders}
        setSelectedPurchaseOrders={setSelectedPurchaseOrders}
        status={status}
        setStatus={setStatus}
        onViewDetails={handleViewPODetails}
        totalCount={purchaseOrdersResponse?.headers?.["x-total-count"] || 0}
      />

      {/* Footer with selection info and actions */}
      {status === "SUBMITTED" && selectedPurchaseOrders?.length > 0 && (
        <div className="absolute bottom-0 left-0 right-0 w-full bg-white px-6 py-2 border-t border-gray-200 z-50">
          <div className="flex justify-between items-center max-w-7xl mx-auto">
            <span className="text-sm">
              Process Purchase Orders
              <span className="text-sm text-gray-600 ml-1">
                {selectedPurchaseOrders?.length || 0} Selected
              </span>
            </span>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => setSelectedPurchaseOrders([])}
                disabled={selectedPurchaseOrders?.length === 0}
              >
                Dismiss
              </Button>
              <Button
                variant="primary"
                onClick={() => alert("Process selected purchase orders")}
                disabled={selectedPurchaseOrders?.length === 0}
              >
                Process Selected
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Filters Modal */}
      {openFilters && (
        <FiltersModal
          open={openFilters}
          onClose={() => setOpenFilters(false)}
          onApply={() => setOpenFilters(false)}
          filters={filters}
          setFilters={setFilters}
        />
      )}

      {/* Create Purchase Order Modal */}
      <CreatePurchaseOrderModal
        open={showCreatePOModal}
        onClose={() => setShowCreatePOModal(false)}
        selectedBranch={selectedBranch}
        allBranchesIds={allBranchesIds}
      />
    </div>
  );
};

export default PurchaseOrders;
